/**
 * Adapter that lifts the existing `IMARewardedVideo` renderer into the
 * `RewardedVideoRenderer` interface the host shell consumes.
 *
 * Translates the underlying `{ granted: boolean }` result into the host's
 * structured `RewardedRenderResult` outcome enum and catches errors so the
 * shell can pick out `autoplay_blocked` vs `error` (the field canary that
 * triggers reverting to a host tap-gate — see ADR-001 risk #1).
 *
 * Activation timing (today): `IMARewardedVideo.show()` calls
 * `AdDisplayContainer.initialize()` synchronously at the top of its body
 * (after a single `await IMALoader.load()`). That places the activation
 * capture AFTER our shell's bid-fetch await, not before — defeating the
 * shell's synchronous-prologue contract. This adapter's
 * `prepareForActivation()` is therefore a STRUCTURAL hook only, not a
 * functional one yet.
 *
 * TODO(rewarded-activation-split): refactor IMARewardedVideo so the
 * adapter can construct + `.initialize()` an AdDisplayContainer in
 * `prepareForActivation()` (the host calls this synchronously inside its
 * prologue) and have `show()` reuse that container. Once that lands, the
 * activation will be captured at the right moment and the
 * `ad_autoplay_blocked` metric should drop.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { IMALoader } from '../ads/ima-loader';
import { IMARewardedVideo } from '../ads/ima-rewarded-video';
export class ImaRewardedHostAdapter {
    constructor(eventEmitter) {
        this.preloaded = false;
        this.inner = new IMARewardedVideo(eventEmitter);
    }
    preload() {
        return __awaiter(this, void 0, void 0, function* () {
            yield IMALoader.load();
            this.preloaded = true;
        });
    }
    isPreloaded() {
        return this.preloaded;
    }
    /**
     * STRUCTURAL hook. See top-of-file TODO — today this is a near-no-op
     * because the underlying IMA renderer creates + initializes the
     * AdDisplayContainer from inside `show()`. We log when called without a
     * preceding `preload()` so the field can spot mis-ordered host code.
     */
    prepareForActivation() {
        if (!this.preloaded) {
            console.warn('[GetJarAdsHost] prepareForActivation called before preload() — IMA SDK not loaded yet; activation may be lost.');
        }
    }
    show(config, vastTag) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield this.inner.show(config, vastTag);
                // The renderer discriminates: IMA SKIPPED → 'skipped', IMA USER_CLOSE
                // + SDK close-X → 'closed'. Both land as granted:false at the game
                // level, but the distinct outcome lets host telemetry / game UX
                // differentiate "user pressed skip" from "user X'd out".
                if (result.granted)
                    return { outcome: 'completed' };
                if (result.reason === 'skipped') {
                    return { outcome: 'skipped', reason: 'user_skip' };
                }
                // Both close paths land as outcome 'closed' to the bridge, but the
                // reason discriminates "user confirmed close in the dialog" from
                // "IMA fired USER_CLOSE / stream ended" — useful for telemetry on
                // how often the mid-stream confirm prompt actually converts.
                const closeReason = result.reason === 'closed_before_complete' ? 'closed_before_complete' : 'user_close';
                return { outcome: 'closed', reason: closeReason };
            }
            catch (err) {
                const name = err instanceof Error ? err.name : '';
                const message = err instanceof Error ? err.message : String(err);
                if (name === 'NotAllowedError' || /NotAllowedError|play\(\)/i.test(message)) {
                    // Field canary — the host shell emits the ad_autoplay_blocked
                    // telemetry on this outcome.
                    return { outcome: 'autoplay_blocked', reason: 'NotAllowedError' };
                }
                // The error string sometimes carries 'No ad available' or similar
                // for no-fill paths. Surface as no_ad when it matches; otherwise
                // a generic error.
                if (/no ad available|no fill|empty/i.test(message)) {
                    return { outcome: 'no_ad', reason: message };
                }
                return { outcome: 'error', reason: message };
            }
        });
    }
}
